import { useState } from 'react';
import { ChevronDown, X } from 'lucide-react';

// American moneyline → decimal payout multiplier; null when the book has no line ("OFF").
export const mlDecimal = (ml) => {
  if (/^even$/i.test(String(ml || '').trim())) return 2;
  const o = parseFloat(String(ml || '').replace('+', ''));
  if (!o) return null;
  return o > 0 ? 1 + o / 100 : 1 + 100 / -o;
};

export const ticketMath = (games, stake) => {
  const chance = Math.min(0.99, games.reduce((a, g) => a * g.pick.prob, 1));
  const dec = games.reduce((a, g) => a * (mlDecimal(g.pick.ml) || 1), 1);
  return { chance, payout: stake * dec, noLine: games.filter((g) => !mlDecimal(g.pick.ml)).length };
};

// 3 tickets, no shared games: Ticket 1 = surest 8, Ticket 2 = next 8, Ticket 3 = next 8.
export const TICKETS = 3;
export const LEGS = 8;
export const autoTickets = (games) => {
  const pool = games.filter((g) => g.state === 'pre').sort((a, b) => b.pick.prob - a.pick.prob);
  return Array.from({ length: TICKETS }, (_, i) => pool.slice(i * LEGS, (i + 1) * LEGS)).filter((t) => t.length);
};

const money = (n) => `$${Math.round(n).toLocaleString()}`;

const LegLine = ({ g, n }) => {
  const p = g.pick;
  const me = g.teams.find((t) => t.abbr === p.abbr);
  const op = g.teams.find((t) => t.abbr !== p.abbr);
  return (
    <li className="flex items-center gap-2 text-[12px] border-t border-zinc-800/60 pt-1 first:border-0 first:pt-0">
      <span className="w-4 text-zinc-500 font-mono text-[10px]">{n}.</span>
      <span className="flex-1 min-w-0 leading-tight">
        <span className="text-white font-semibold">{me?.rank ? `#${me.rank} ` : ''}{p.team}</span> <span className="text-zinc-500">({me?.record})</span>
        <span className="block text-[10px] text-zinc-500">to win {me?.home_away === 'home' ? 'vs' : '@'} {op?.rank ? `#${op.rank} ` : ''}{op?.name} ({op?.record}) · <span className="whitespace-nowrap">ML {p.ml && p.ml !== 'OFF' ? p.ml : 'not offered'}</span></span>
      </span>
      <span className="font-mono text-[11px] font-bold text-emerald-300">{Math.min(99, Math.round(p.prob * 100))}%</span>
    </li>
  );
};

const TicketCard = ({ legs, idx, stake, onUse }) => {
  const m = ticketMath(legs, stake);
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-violet-500/30 bg-zinc-950/60 p-3" data-testid={`cfb-auto-ticket-${idx}`}>
      <div className="flex items-baseline gap-2 mb-1.5">
        <p className="text-sm font-bold text-white flex-1">Ticket {idx + 1} <span className="font-normal text-zinc-500">· {legs.length} legs{legs.length < LEGS ? ' (not enough games left)' : ''}</span></p>
        <span className="font-mono text-sm font-bold text-violet-300" data-testid={`cfb-auto-ticket-${idx}-chance`}>{Math.round(m.chance * 100)}% all win</span>
      </div>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`cfb-auto-ticket-${idx}-toggle`}
        className="flex items-center gap-1 text-[11px] text-violet-200 hover:text-white transition-colors">
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        {open ? 'Hide picks' : legs.map((g) => g.pick.abbr).join(' · ')}
      </button>
      {open && (
        <ol className="space-y-1 mt-1" data-testid={`cfb-auto-ticket-${idx}-legs`}>
          {legs.map((g, i) => <LegLine key={g.event_id} g={g} n={i + 1} />)}
        </ol>
      )}
      <div className="flex items-center gap-2 mt-2">
        <p className="text-[11px] text-zinc-500 flex-1" data-testid={`cfb-auto-ticket-${idx}-payout`}>
          {m.noLine === legs.length ? 'No sportsbook lines yet — payout shows once lines post' : <>{money(stake)} returns about <span className="text-emerald-300 font-semibold">{money(m.payout)}</span>{m.noLine ? ` · ${m.noLine} leg${m.noLine > 1 ? 's' : ''} with no ML not counted in payout` : ''}</>}
        </p>
        <button type="button" onClick={() => onUse(legs)} data-testid={`cfb-auto-ticket-${idx}-use`}
          className="rounded-full bg-violet-500/20 px-3 py-1 text-xs font-semibold text-violet-200 hover:bg-violet-500/30 transition-colors">Show on board</button>
      </div>
    </div>
  );
};

export const AutoTickets = ({ games, stake, setStake, onUse }) => {
  const tickets = autoTickets(games);
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 space-y-2" data-testid="cfb-auto-tickets">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-bold flex-1">3 straight-win tickets · 8 legs · no game repeats</p>
        <label className="flex items-center gap-1 text-xs text-zinc-400">Stake $
          <input type="number" min="1" value={stake} onChange={(e) => setStake(Math.max(1, Number(e.target.value) || 1))} data-testid="cfb-stake-input"
            className="w-16 rounded bg-zinc-800 px-2 py-1 text-white text-xs" />
        </label>
      </div>
      {!tickets.length ? <p className="text-xs text-zinc-500" data-testid="cfb-auto-tickets-empty">No upcoming games left for this day.</p>
        : tickets.map((t, i) => <TicketCard key={i} legs={t} idx={i} stake={stake} onUse={onUse} />)}
      <p className="text-[10px] text-zinc-600">Straight wins only (moneyline) — no spreads. Each team just has to win its game. "% all win" = chance every leg wins outright. Ticket 1 gets the surest picks, then 2, then 3. Payouts are estimates from the listed odds.</p>
    </div>
  );
};

export const BuilderBar = ({ selected, stake, onClear }) => {
  if (!selected.length) return null;
  const m = ticketMath(selected, stake);
  return (
    <div className="sticky bottom-3 z-20 rounded-xl border border-emerald-500/40 bg-zinc-950/90 backdrop-blur-md p-3 flex items-center gap-3 shadow-xl" data-testid="cfb-builder-bar">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-white" data-testid="cfb-builder-summary">{selected.length} picked · <span className="text-emerald-300">{Math.round(m.chance * 100)}% all win outright</span></p>
        <p className="text-[11px] text-zinc-400" data-testid="cfb-builder-payout">{money(stake)} returns about {money(m.payout)}{m.noLine ? ` · ${m.noLine} with no moneyline` : ''}</p>
      </div>
      <button type="button" onClick={onClear} data-testid="cfb-builder-clear" className="rounded-full bg-zinc-800 p-2 text-zinc-300 hover:bg-zinc-700 transition-colors" aria-label="Clear"><X className="w-4 h-4" /></button>
    </div>
  );
};
